import React, { useEffect, useState, useCallback, useContext } from "react";
import { Button, TextField } from "@mui/material";
import { styled } from "@material-ui/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";

// Componentes locais
import Header from "../../components/Header";
import IconsBar from "../../components/IconsBar/IconsBar";
import { MultipleClassroomSelect } from "../../components/MultipleClassroomSelect";
import ImageSkeleton from "./ImageSkeleton";
import { Loading } from "../../components/Loading/Loading";
import { AlertMessages } from "../../components/AlertMessages";

// API e serviços
import { sendOneSignalAnnouncementsBulk } from "../../api/autenticoAPI";
import { getAllClassrooms } from "../../api/sponteAPI";
import { sendOneSignalNotifications } from "../../services/oneSignal";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import { anoLetivo, getOneSignalUsers } from "../../utils/YEARCONSTS";

// Estilos
import {
  ActionsArea,
  AttachmentsArea,
  Container,
  ContentArea,
  DividerAreaLeft,
  DividerAreaRight,
  FormItens,
  PageTitle,
  UsersInfo,
} from "./styles";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const Announcements = () => {
  const [selectedImage, setSelectedImage] = useState("");
  const [anexosSelecionados, setAnexosSelecionados] = useState([]);
  const [linksInseridos, setLinksInseridos] = useState([]);
  // `classrooms` guarda apenas os IDs das turmas selecionadas
  const [classrooms, setClassrooms] = useState([]);
  const [title, setTitle] = useState("");
  const [usersNotifications, setUsersNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [typeMessage, setTypeMessage] = useState("success");
  const [contentMessage, setContentMessage] = useState("teste");
  const [classroomsName, setClassroomsName] = useState([]);

  const auth = useContext(AuthContext);

  const resetFields = useCallback(() => {
    setClassrooms([]);
    setTitle("");
    setUsersNotifications([]);
    setSelectedImage("");
    setAnexosSelecionados([]);
    setLinksInseridos([]);
  }, []);

  useEffect(() => {
    if (classrooms.length > 0 && !classrooms[0]?.hasOwnProperty("TurmaID")) {
      setUsersNotifications([]);
    }
  }, [classrooms]);

  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        const res = await getAllClassrooms(anoLetivo);
        setClassroomsName(res);
      } catch (error) {
        console.error("Erro ao carregar salas:", error);
        setTypeMessage("error");
        setContentMessage("Erro ao carregar salas");
        setShow(true);
      }
    };

    fetchClassrooms();
  }, []);

  const getUsers = useCallback(async () => {
    try {
      setLoading(true);

      // classrooms já é um array de IDs selecionados (controlado pelo MultipleClassroomSelect)
      if (!classrooms || classrooms.length === 0) {
        setUsersNotifications([]);
        setTypeMessage("error");
        setContentMessage(
          "Selecione pelo menos uma turma antes de carregar os destinatários."
        );
        setShow(true);
        return;
      }

      const oneSignalUsers = await getOneSignalUsers(classrooms);
      const flattenedUsers = oneSignalUsers.flatMap((user) => user.users);
      setUsersNotifications(flattenedUsers);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      setTypeMessage("error");
      const errorMessage = error?.message || "Erro ao buscar usuários";
      setContentMessage(
        errorMessage.includes("Nenhum ID de turma válido")
          ? "Erro: IDs de turma inválidos. Selecione as turmas novamente."
          : errorMessage.includes("array não vazio")
          ? "Selecione pelo menos uma turma antes de carregar os destinatários."
          : `Erro ao buscar usuários: ${errorMessage}`
      );
      setShow(true);
      setUsersNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [classrooms]);

  const sendFile = useCallback(async () => {
    // Validações básicas antes de qualquer envio
    if (!title || !title.trim()) {
      setTypeMessage("error");
      setContentMessage("Informe o título do comunicado.");
      setShow(true);
      return;
    }

    // Validação inicial - verificar se há turmas selecionadas
    if (!classrooms || !Array.isArray(classrooms) || classrooms.length === 0) {
      setTypeMessage("error");
      setContentMessage("Selecione pelo menos uma turma para enviar o comunicado.");
      setShow(true);
      return;
    }

    if (!usersNotifications || usersNotifications.length === 0) {
      setTypeMessage("error");
      setContentMessage(
        "Nenhum destinatário encontrado para as turmas selecionadas. Clique em \"Carregar destinatários\" antes de enviar."
      );
      setShow(true);
      return;
    }

    try {
      setLoading(true);

      // Função auxiliar para extrair ID de diferentes formatos
      const extractClassroomId = (value) => {
        // Se for objeto com TurmaID, extrair o ID
        if (value && typeof value === "object" && !Array.isArray(value) && value.TurmaID !== undefined) {
          const id = Number(value.TurmaID);
          return isNaN(id) ? null : id;
        }
        // Se já for número, usar diretamente
        if (typeof value === "number" && !isNaN(value)) {
          return value;
        }
        // Se for string numérica, converter
        if (typeof value === "string" && value.trim() !== "") {
          const num = Number(value.trim());
          return isNaN(num) ? null : num;
        }
        return null;
      };

      // classrooms pode vir como array de números ou array de arrays
      // Normalizar para array plano de números válidos
      const turmaIds = [];
      
      for (const classroom of classrooms) {
        // Se for um array aninhado, processar cada elemento
        if (Array.isArray(classroom)) {
          for (const item of classroom) {
            const id = extractClassroomId(item);
            if (id !== null) {
              turmaIds.push(id);
            }
          }
        } else {
          // Se não for array, processar diretamente
          const id = extractClassroomId(classroom);
          if (id !== null) {
            turmaIds.push(id);
          }
        }
      }

      // Validar se há IDs válidos
      if (!turmaIds || turmaIds.length === 0) {
        setTypeMessage("error");
        setContentMessage("Erro ao processar as turmas selecionadas. Por favor, selecione novamente.");
        setShow(true);
        setLoading(false);
        return;
      }

      // Normalizar nomes de turma
      // Garantir que temos a lista de turmas carregada
      if (!classroomsName || classroomsName.length === 0) {
        setTypeMessage("error");
        setContentMessage("Erro: lista de turmas não carregada. Por favor, recarregue a página.");
        setShow(true);
        setLoading(false);
        return;
      }

      const turmaNames = turmaIds.map((turmaId) => {
        // Normalizar o ID da turma para comparação (pode ser número ou string)
        const turmaIdNormalizado = Number(turmaId);
        
        // Buscar o nome da turma, normalizando o TurmaID do item também
        const className = classroomsName.find((item) => {
          // TurmaID pode vir como array ou valor direto (conforme normalização do backend)
          let itemTurmaID = item.TurmaID;
          if (Array.isArray(itemTurmaID)) {
            itemTurmaID = itemTurmaID[0];
          }
          
          // Comparar normalizando ambos para número
          const itemIdNormalizado = Number(itemTurmaID);
          return !isNaN(itemIdNormalizado) && !isNaN(turmaIdNormalizado) && 
                 itemIdNormalizado === turmaIdNormalizado;
        });
        
        if (className) {
          // Nome pode vir como array ou string direta
          let nome = className.Nome;
          if (Array.isArray(nome)) {
            nome = nome[0];
          }
          nome = String(nome || "").trim();
          
          if (nome.length > 0 && nome !== "undefined" && nome !== "null") {
            return nome;
          }
        }
        
        // Usar o ID como fallback apenas se realmente não encontrar
        return String(turmaId);
      });

      // Validar se há nomes válidos (não apenas IDs)
      const nomesValidos = turmaNames.filter((nome, index) => {
        const turmaId = turmaIds[index];
        // Se o nome for igual ao ID, significa que não encontrou o nome real
        return nome && nome.trim().length > 0 && nome !== String(turmaId);
      });
      
      if (nomesValidos.length === 0) {
        setTypeMessage("error");
        setContentMessage("Erro ao obter nomes das turmas selecionadas. Por favor, recarregue a página.");
        setShow(true);
        setLoading(false);
        return;
      }
      
      // Se alguns nomes não foram encontrados, usar os IDs como fallback
      const turmaNamesFinal = turmaNames;

      // Montar um único FormData para upload em lote
      const fData = new FormData();
      if (selectedImage) {
        fData.append("announcement", selectedImage);
      }
      anexosSelecionados.forEach((file) => {
        fData.append("attachments", file);
      });
      linksInseridos.forEach((link) => {
        fData.append("links", link);
      });

      fData.append("title", title);
      fData.append("classroom_ids", JSON.stringify(turmaIds));
      fData.append("classroom_names", JSON.stringify(turmaNamesFinal));

      let uploadSuccess = 0;
      let uploadFailed = 0;

      try {
        const uploadResult = await sendOneSignalAnnouncementsBulk(fData);
        uploadSuccess = uploadResult?.totalClassrooms || turmaIds.length;
      } catch (uploadError) {
        console.error("Erro no upload em lote de comunicados:", uploadError);
        uploadFailed = turmaIds.length;
      }

      // Se nenhum upload teve sucesso, não faz sentido enviar notificações
      if (uploadSuccess === 0) {
        setTypeMessage("error");
        setContentMessage(
          "Falha ao enviar os arquivos do comunicado. Nenhuma turma foi atualizada."
        );
        setShow(true);
        setLoading(false);
        return;
      }

      const notificationResults = await sendOneSignalNotifications({
        name: title,
        unit: auth.user.unit,
        ids: usersNotifications,
        message: "Novo comunicado do Colégio Autêntico!",
      });

      const notifSuccess = Array.isArray(notificationResults)
        ? notificationResults.filter((r) => r.success).length
        : 0;
      const notifFailed =
        Array.isArray(notificationResults) && usersNotifications
          ? usersNotifications.length - notifSuccess
          : 0;

      setContentMessage(
        `Comunicados enviados! Uploads: ${uploadSuccess}/${
          uploadSuccess + uploadFailed
        } turmas com sucesso, ${uploadFailed} falhas. Notificações: ${
          notifSuccess || 0
        } usuários notificados, ${notifFailed > 0 ? notifFailed : 0} falhas.`
      );
      setTypeMessage("success");
      setShow(true);
      resetFields();
    } catch (error) {
      console.error("Erro ao enviar comunicados:", error);
      setTypeMessage("error");
      setContentMessage(
        "Erro ao enviar comunicados. Tente novamente em alguns instantes."
      );
      setShow(true);
    } finally {
      setLoading(false);
    }
  }, [
    classrooms,
    classroomsName,
    selectedImage,
    anexosSelecionados,
    linksInseridos,
    title,
    auth.user.unit,
    usersNotifications,
    resetFields,
  ]);

  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    setAnexosSelecionados((prev) => [...prev, ...files]);
  }, []);

  const handleLinkInsert = useCallback(() => {
    const link = prompt("Insira o link desejado:");
    if (link?.trim()) {
      setLinksInseridos((prev) => [...prev, link.trim()]);
    }
  }, []);

  return (
    <Container>
      <Header />
      <IconsBar />
      {loading && <Loading />}
      <ContentArea>
        <PageTitle>Comunicados</PageTitle>
        <FormItens encType="multipart/form-data">
          <ActionsArea>
            <DividerAreaLeft>
              <MultipleClassroomSelect
                classrooms={classrooms}
                setClassrooms={setClassrooms}
              />
              <TextField
                value={title}
                onChange={({ target }) => setTitle(target.value.toUpperCase())}
                label="Título do Comunicado"
                fullWidth
                variant="outlined"
                autoComplete="off"
              />
              <ImageSkeleton file={selectedImage} />
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                style={{ backgroundColor: "#1E958C" }}
              >
                Carregar Arquivo
                <VisuallyHiddenInput
                  type="file"
                  onChange={(e) => setSelectedImage(e.target.files[0])}
                />
              </Button>
            </DividerAreaLeft>
            <DividerAreaRight>
              <Button
                onClick={getUsers}
                component="label"
                variant="contained"
                startIcon={<RefreshIcon />}
                style={{ backgroundColor: "#1E958C" }}
              >
                Carregar destinatários
              </Button>
              {usersNotifications.length > 0 ? (
                <UsersInfo>
                  Existem {usersNotifications.length} usuários aptos a receber
                  esse comunicado!
                </UsersInfo>
              ) : (
                <UsersInfo>
                  Não existem usuários aptos a receber esse comunicado, ou
                  nenhuma turma foi selecionada!
                </UsersInfo>
              )}
              <AttachmentsArea>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  style={{ backgroundColor: "#1E958C" }}
                >
                  Inserir Anexos
                  <input
                    type="file"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    multiple
                  />
                </Button>
                <div>
                  {anexosSelecionados.map((file, index) => (
                    <p key={`${file.name}-${index}`}>{file.name}</p>
                  ))}
                </div>
                <Button
                  onClick={handleLinkInsert}
                  component="label"
                  variant="contained"
                  startIcon={<LinkIcon />}
                  style={{ backgroundColor: "#1E958C" }}
                >
                  Inserir Links
                </Button>
                <div>
                  {linksInseridos.map((link, index) => (
                    <p key={`${link}-${index}`}>{link}</p>
                  ))}
                </div>
              </AttachmentsArea>
              <Button
                onClick={sendFile}
                startIcon={<SendIcon />}
                variant="contained"
                style={{ height: 70, width: "80%" }}
                disabled={usersNotifications.length === 0}
              >
                Enviar Comunicados
              </Button>
            </DividerAreaRight>
          </ActionsArea>
        </FormItens>
      </ContentArea>
      <AlertMessages
        show={show}
        typeMessage={typeMessage}
        contentMessage={contentMessage}
        setShow={setShow}
      />
    </Container>
  );
};

export default Announcements;
